'use client';

import { forwardRef } from 'react';
import classnames from 'classnames';
import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import styles from './ProgressBar.module.scss';

type Theme = 'dark' | 'light';
type ProgressBarSize = 'small' | 'medium' | 'large';
type ProgressBarVariant = 'default' | 'success' | 'warning' | 'error' | 'black' | 'neutral';

export type ProgressBarProps = {
  /** Test ID for the component */
  dataTestId: string;
  /** Current progress value (0-100) */
  value: number;
  /** Maximum value @default 100 */
  max?: number;
  /** Theme variant @default 'dark' */
  theme?: Theme;
  /** Size variant @default 'small' */
  size?: ProgressBarSize;
  /** Color variant @default 'default' */
  variant?: ProgressBarVariant;
  /** Show percentage label @default false */
  showPercentage?: boolean;
  /** Optional label text displayed center-aligned below the progress bar */
  label?: string;
  /** Enable stripe animation @default false */
  animated?: boolean;
  /** Indeterminate state (loading without known progress) @default false */
  indeterminate?: boolean;
  /** Additional CSS class */
  className?: string;
};

/**
 * ProgressBar component for displaying progress or loading states
 *
 * @component
 * @example
 * ```tsx
 * <ProgressBar
 *   dataTestId="upload-progress"
 *   value={75}
 *   theme="light"
 *   showPercentage
 *   label="Uploading..."
 * />
 * ```
 */
const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      dataTestId,
      value,
      max = 100,
      theme = 'dark',
      size = 'small',
      variant = 'default',
      showPercentage = false,
      label,
      animated = false,
      indeterminate = false,
      className,
    },
    ref
  ) => {
    // Clamp value between 0 and max
    const clampedValue = Math.min(Math.max(0, value), max);
    const percentage = Math.round((clampedValue / max) * 100);

    return (
      <Box
        ref={ref}
        className={classnames(
          styles.progressBar,
          styles[theme],
          styles[size],
          styles[variant],
          {
            [styles.animated]: animated && !indeterminate,
            [styles.indeterminate]: indeterminate,
          },
          className
        )}
        data-testid={dataTestId}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : clampedValue}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label || `Progress: ${percentage}%`}
      >
        <Box className={styles.track}>
          <div
            className={classnames(styles.fill, {
              [styles[`progress-${percentage}`]]: !indeterminate,
            })}
            data-progress={percentage}
            data-testid={`${dataTestId}-fill`}
          />
        </Box>
        {(label || showPercentage) && (
          <Box className={styles.labelContainer}>
            {label && (
              <Typography variant="body" size="extraSmall" theme={theme} className={styles.label}>
                {label}
              </Typography>
            )}
            {showPercentage && !indeterminate && (
              <Typography
                variant="body"
                size="extraSmall"
                theme={theme}
                className={styles.percentage}
              >
                {percentage}%
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
